/**
 * Query hooks of the family slice (contract §3). Keys come from `qk` so an
 * SSE change for the job invalidates them.
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { paths } from "@/api/client";
import { useApi } from "@/api/hooks";
import { qk } from "@/api/keys";
import { useViewParams } from "@/views/holders_pnl/params";
import type {
  ActionPage,
  FamilyCard,
  FamilyGraph,
  FamilyRankHistory,
  FamilyRecipients,
  FamilyTimeline,
  GraphQuery,
  RanksQuery,
  TimelineQuery,
} from "@/api/types";

export interface JobRef {
  chain: string;
  token: string;
}

function familyPath(job: JobRef, address: string): string {
  return `${paths.job(job.chain, job.token)}/families/${address.toLowerCase()}`;
}

export function useFamilyCard(job: JobRef | undefined, address: string | undefined) {
  const { bool } = useViewParams();
  const inheritTransfers = bool("inherit_transfers");
  const chain = job?.chain ?? "";
  const token = job?.token ?? "";
  const addr = (address ?? "").toLowerCase();
  const params = { inherit_transfers: inheritTransfers || undefined };
  return useApi<FamilyCard>(
    [...qk.family(chain, token, addr), params],
    familyPath({ chain, token }, addr),
    params,
    { enabled: !!job && !!addr },
  );
}

export function useFamilyTimeline(
  job: JobRef | undefined,
  address: string | undefined,
  params: TimelineQuery,
  enabled = true,
) {
  const { bool } = useViewParams();
  const inheritTransfers = bool("inherit_transfers");
  const query = { ...params, inherit_transfers: inheritTransfers || undefined };
  const chain = job?.chain ?? "";
  const token = job?.token ?? "";
  const addr = (address ?? "").toLowerCase();
  return useApi<FamilyTimeline>(
    qk.familyTimeline(chain, token, addr, query),
    `${familyPath({ chain, token }, addr)}/timeline`,
    query,
    {
      enabled: enabled && !!job && !!addr,
      placeholderData: (prev) =>
        (prev?.data.inherit_transfers ?? false) === inheritTransfers ? prev : undefined,
    },
  );
}

/**
 * Block -> unix seconds for the family's events, so the LP strategies card
 * can say when each position was opened and closed (positions carry blocks
 * only). One per-event timeline over the family's whole life: served from
 * flow-web's cache, no RPC.
 */
export function useFamilyBlockTimes(job: JobRef | undefined, address: string | undefined, enabled = true) {
  const timeline = useFamilyTimeline(job, address, {}, enabled);
  const points = timeline.data?.data.points;
  return useMemo(() => {
    if (!points) return undefined;
    const times = new Map(points.map((p) => [p.block, p.t]));
    return (block: number) => times.get(block);
  }, [points]);
}

/**
 * The holder's place in the leaderboard over time. Its own request rather
 * than a field of the timeline: the timeline replays this family's events,
 * while a rank is a fact about everyone else, and rebuilding the census
 * costs enough that only the chart showing it should pay.
 */
export function useFamilyRanks(
  job: JobRef | undefined,
  address: string | undefined,
  params: RanksQuery,
  enabled = true,
) {
  const chain = job?.chain ?? "";
  const token = job?.token ?? "";
  const addr = (address ?? "").toLowerCase();
  return useApi<FamilyRankHistory>(
    qk.familyRanks(chain, token, addr, params),
    `${familyPath({ chain, token }, addr)}/ranks`,
    { ...params },
    { enabled: enabled && !!job && !!addr, placeholderData: (prev) => prev },
  );
}

export function useFamilyGraph(job: JobRef | undefined, address: string | undefined, params: GraphQuery) {
  const chain = job?.chain ?? "";
  const token = job?.token ?? "";
  const addr = (address ?? "").toLowerCase();
  return useApi<FamilyGraph>(
    qk.familyGraph(chain, token, addr, params),
    `${familyPath({ chain, token }, addr)}/graph`,
    { ...params },
    { enabled: !!job && !!addr, placeholderData: (prev) => prev },
  );
}

/** Wallets outside the family that members sent tokens to (`GET …/recipients`). */
export function useFamilyRecipients(job: JobRef, address: string) {
  const addr = address.toLowerCase();
  return useApi<FamilyRecipients>(
    qk.familyRecipients(job.chain, job.token, addr),
    `${familyPath(job, addr)}/recipients`,
  );
}

/** The family's full tape (`GET …/actions?family=`), for the "show all" mode of the actions card. */
export function useFamilyActions(
  job: JobRef | undefined,
  family: string | undefined,
  limit: number,
  enabled: boolean,
) {
  const chain = job?.chain ?? "";
  const token = job?.token ?? "";
  const fam = (family ?? "").toLowerCase();
  const params = { family: [fam], limit };
  return useApi<ActionPage>(qk.actions(chain, token, params), `${paths.job(chain, token)}/actions`, params, {
    enabled: enabled && !!job && !!fam,
  });
}

/**
 * Quantize a resolved range so a pinned "now" does not change the query key
 * on every render: `from` floors and `to` ceils to the bucket the series will
 * use. The bucket is chosen by the caller (`viewBucket`: URL bucket, else the
 * URL range's auto) — never from this range alone, which may be life-clamped
 * and would then disagree with the top bar.
 */
export function stableRange(
  range: { from: number; to: number },
  bucket: number,
): { from: number; to: number; bucket: number } {
  const b = Math.max(1, bucket);
  const from = Math.floor(range.from / b) * b;
  const to = Math.ceil(range.to / b) * b;
  return { from, to: to > from ? to : from + b, bucket: b };
}

/** A view-local URL param (graph depth, table toggle…) that round-trips like the global state. */
export function useViewParam(key: string, fallback: string): [string, (v: string | undefined) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? fallback;
  const set = useCallback(
    (v: string | undefined) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === undefined || v === fallback) next.delete(key);
          else next.set(key, v);
          return next;
        },
        { replace: true },
      ),
    [key, fallback, setParams],
  );
  return [value, set];
}
